import {
  createReportConfiguration,
  createReportCopy,
  createSavedView,
  deleteReportById,
  getFullNameByUuid,
  getLens,
  getOgqlQueries,
  getReportConfigurationById,
  IReportCreator,
  platform,
  reportOgqlView,
  syncEmbedByReportIds
} from '@opengov/api-deprecated';
import { changeReportDataset } from '@opengov/reporting-api';
import { SegmentEffects } from '@opengov/app-core/analytics';
import { selectCurrentEntity } from '@opengov/app-core/entity';
import { getNumericalEntityId } from '@opengov/app-core/entity/selectors';
import { transport } from '@opengov/app-core/transport';
import { modalActionCreators } from '@opengov/component-library-classic/module/modals';
import { IRecordOf } from '@opengov/compose-app/immutable';
import { createSelector } from '@opengov/compose-app/reselect';
import { selectUseQueryService } from '@opengov/ogql-client/selectors';
import { IOgqlColumn } from '@opengov/ogql-types';
import {
  createDetailTableQuery,
  createReportConfiguration as createReportConfigurationFactory,
  IAgGridColumnConfig,
  IDatasetChangeMapping,
  IDatasetLensColumn,
  IEntity,
  IPaginatedReportsResponse,
  IReport,
  IReportConfiguration,
  IReportDatasetLens,
  IReportGalleryFilters,
  IReportGalleryPagination,
  IReportResponse,
  ITemplatedSavedView
} from '@opengov/reporting-types';
import { getColumnName, IReportColumn } from '@opengov/reporting-types/src/reportColumns-immer';
import { ISavedView, ISavedViewResponse } from '@opengov/reporting-types/src/reportSavedView';
import { produce } from 'immer';
import { List, Map, Record } from 'immutable';
import moment from 'moment';
import { NavigateFunction } from 'react-router-dom';

import { composeEffects } from '../../../../module';
import {
  baseReportsV2ServiceUrl,
  baseReportsV3ServiceUrl,
  makeReportExplorerPathReactRouterVersion,
  reportGalleryPath,
  reportGalleryPathReactRouterVersion,
  reportSavedViewEditPath,
  selectReportExplorerPath,
  selectSavedViewPath
} from '../../../../module/routes/paths';
import { TError } from '../../../common/errors';
import { ReportCreatePayload } from '../../../common/report/records';
import { selectNewReport } from '../../../reportCreate/selectors/selectReportCreateState';
import { reportCreateActionCreators } from '../../../reportCreate/state/reportCreateState';
import {
  selectCurrentSourceColumns,
  selectDetailTableColumnNamesMap,
  selectReportConfigWithMetadata
} from '../../../reportExplorer/selectors/selectReportExplorerState';
import { reportExplorerActionCreators } from '../../../reportExplorer/state/reportExplorerState';
import { SettingsCardMode } from '../../../reportExplorer/types/reportExplorerTypes';
import { selectCurrentSavedViewEditSavedView } from '../../../reportSavedViewEdit/selectors/selectReportSavedViewEdit';
import { selectModifiedReport } from '../../../reportSettings/selectors/selectModifiedReport';
import { selectNextGenGalleryPhase1Enabled, selectUseLargeScaleQueryLimit } from '../../flags';
import { reportHistoryActionCreators } from '../../history/state';
import { reportLocaleText } from '../../locale';
import { shareLinkActionCreators } from '../../modals/components/ShareModal/state';
import { ReportModalTypes } from '../../modals/types';
import { columnSearchQuery } from '../../reportConfiguration/effects/queries/columnSearchQuery';
import { selectCurrentReportConfiguration, selectIsInStandardReport } from '../../reportConfiguration/selectors';
import { reportConfigurationActionCreators } from '../../reportConfiguration/state';
import { selectCurrentLens, selectCurrentTemplateId } from '../../reportTemplate/standardTemplateSelectors';
import { selectCurrentSavedView, selectSavedViews } from '../../savedViews/selectors';
import {
  selectCurrentReport,
  selectDatasetLensesForReports,
  selectFilters,
  selectPagination,
  selectReportCreators,
  selectReports
} from '../selectors/reportSelectors';
import { reportActionCreators } from '../state/reportState';
import { IReportCreatePayload } from '../types';
import { reportDimensionalEffects } from './../../../reportExplorer/effects/reportDimensionalEffects';
import { hardNavigate } from '../../../../module/routes/routesUtils';
import { showPermissionErrorToast } from '../../helpers/permissionErrorHelper';
import { MuiToastTypes } from '../../helpers/utils';

export type ToastPayload = { headerText: string; bodyText: string };

const reportsControllerUrl = `${baseReportsV2ServiceUrl}/reports`;

const reportsV3ControllerUrl = `${baseReportsV3ServiceUrl}/reports`;

const reportPermalinkUrl = `${baseReportsV2ServiceUrl}/permalinks`;

const addOrigin = (uri) => `${window.location.origin}${uri}`;

//Function that sends out request to create a report
export const postReport = async (e: IReportCreatePayload) =>
  transport
    .url(reportsControllerUrl)
    .json(e)
    .post()
    .json()
    .then((report: IReport) => {
      void SegmentEffects.track({ eventName: 'Report: Report: CreateReport', props: report }).catch(() => null);
      reportExplorerActionCreators.setAllowedActions(Map(report.allowedActions));
      reportActionCreators.loadReportsSuccess([report]);
      return report;
    })
    .catch((err: TError) => {
      showPermissionErrorToast(err);
      throw Object.assign(new Error(err.message ?? 'Unknown error occurred'), { status: err.status });
    });

export const copySavedViewForStandardReport = (
  savedView: ITemplatedSavedView,
  newReportId: string
): Promise<ISavedViewResponse> => {
  const updatedRC = { ...savedView.reportConfiguration, id: undefined, reportId: newReportId };
  return createReportConfiguration(updatedRC as any)
    .then((newRC) =>
      createSavedView({
        entityId: savedView.entityId,
        reportId: newReportId,
        title: savedView.title,
        reportConfiguration: newRC.id,
        description: savedView.description
      })
    )
    .catch((err: TError) => {
      showPermissionErrorToast(err);
      throw new Error(err.message || 'Unknown error occurred');
    });
};

export const copySavedViewForCustomReport = (
  savedView: ISavedView,
  newReportId: string
): Promise<ISavedViewResponse> => {
  return getReportConfigurationById(savedView.reportConfiguration)
    .then((rc) => {
      rc.id = null;
      rc.reportId = newReportId;
      return createReportConfiguration(rc).then((newRC) =>
        createSavedView({
          entityId: savedView.entityId,
          reportId: newReportId,
          title: savedView.title,
          reportConfiguration: newRC.id,
          description: savedView.description
        })
      );
    })
    .catch((err: TError) => {
      showPermissionErrorToast(err);
      throw new Error(err.message || 'Unknown error occurred');
    });
};

export const reportConfigurationWithoutFilters = (reportConf: IReportConfiguration): IReportConfiguration => {
  const rc = JSON.parse(JSON.stringify(reportConf));
  rc.detailTableQuery = createDetailTableQuery({ ...rc.detailTableQuery, filter: [] });
  return rc;
};

export const sortColumnsIntoAgGridOrder = (
  unsortedColumns: IReportColumn[],
  agGridColumnConfig: IAgGridColumnConfig[]
): IReportColumn[] => {
  const reordered = unsortedColumns.sort((a, b) => {
    const indexA = agGridColumnConfig?.find((col) => col.columnName === a.name)?.sortedIndex;
    const indexB = agGridColumnConfig?.find((col) => col.columnName === b.name)?.sortedIndex;
    return indexA < indexB ? -1 : 0;
  });
  const leftCols = [];
  const rightCols = [];
  const unpinnedCols = [];
  reordered.forEach((col) => {
    const columnPinned = agGridColumnConfig?.find((config) => config.columnName === col.name)?.pinned;
    switch (columnPinned) {
      case 'left':
        leftCols.push(col);
        break;
      case 'right':
        rightCols.push(col);
        break;
      default:
        unpinnedCols.push(col);
    }
  });
  return leftCols.concat(unpinnedCols, rightCols);
};

// the config we use to generate the export might have its filters removed based on the user's selection
// we also need to ensure that column ordering and visibility matches what is currently displayed in the table
export const reportConfigForExport = (
  reportConfig: IReportConfiguration,
  sourceColumns: IDatasetLensColumn[] | IOgqlColumn[],
  filtered: boolean
): IReportConfiguration => {
  const rc = filtered ? JSON.parse(JSON.stringify(reportConfig)) : reportConfigurationWithoutFilters(reportConfig);
  const { columns } = rc.detailTableQuery;
  const colsWithAgGridVisibility = columns.map((col) => {
    const agGridColumnConfig =
      rc.presentationConfiguration.agGridColumnConfig &&
      rc.presentationConfiguration.agGridColumnConfig.find((column) => column.columnName === col.name);
    const sourceColumn =
      (sourceColumns as any).find((c) => c.name === col.name) ||
      (sourceColumns as any).find((c) => c.name === getColumnName(col.name));
    let hiddenInSource = false;
    if (sourceColumn) {
      hiddenInSource = 'visible' in sourceColumn ? !sourceColumn.visible : sourceColumn.metadata?.tags?.hidden;
    }
    const hiddenInReportConfig = !!(agGridColumnConfig && agGridColumnConfig.hide);
    return { ...col, hidden: col.hidden || hiddenInReportConfig || hiddenInSource };
  });

  const colConfig = rc.presentationConfiguration.agGridColumnConfig;

  if (!colConfig) {
    rc.detailTableQuery = createDetailTableQuery({ ...rc.detailTableQuery, columns: colsWithAgGridVisibility });
    return rc;
  }

  const updatedColumns = sortColumnsIntoAgGridOrder(colsWithAgGridVisibility, colConfig);

  const tableQuery = createDetailTableQuery({ ...rc.detailTableQuery, columns: updatedColumns });
  rc.detailTableQuery = tableQuery;

  return rc;
};

export const getReportUsers = async (
  reportsWithCreatedUserName: IReportResponse[],
  getUserById: (id: string) => Promise<IReportCreator>
): Promise<[IReportResponse[], IReportCreator[]]> => {
  const missingUserNameUuids = reportsWithCreatedUserName
    .filter((report) => !report.createdByUser)
    .map((report) => report.createdBy)
    .filter((userId, index, self) => self.indexOf(userId) === index)
    .map((uuid) => uuid);

  if (missingUserNameUuids.length > 0) {
    const missingUserData = (
      await Promise.allSettled(missingUserNameUuids.map((userId) => getUserById(userId)))
    ).flatMap((promiseResult) => (promiseResult.status === 'fulfilled' ? promiseResult.value : []));
    const reportsWithAllCreatedUserName = reportsWithCreatedUserName.map((report) =>
      report.createdByUser
        ? report
        : {
            ...report,
            createdByUser: missingUserData.find((user) => user.uuid === report.createdBy)
          }
    );
    return [reportsWithAllCreatedUserName, missingUserData];
  }
  return [reportsWithCreatedUserName, []];
};

export const getLensesById = async (ids: string[]): Promise<void> => {
  const lenses: IReportDatasetLens[] = [];
  for (let i = 0; i < ids.length; i++) {
    const lensId = ids[i];
    let lens;
    try {
      lens = await getLens(lensId);
      lenses.push(lens);
    } catch (err) {
      console.error(`Failed to get Lens: ${err.message}`);
    }
  }
  reportActionCreators.loadDataLensesSuccess(lenses);
};

export const loadMissingReportLenses = async (
  reportsWithLensId: IReportResponse[],
  loadedLenses: List<IRecordOf<IReportDatasetLens>>
): Promise<void> => {
  const missingLensIds = reportsWithLensId
    .map((report) => (report.isMultiDataset ? report.datasetIds : report.lensId))
    .flat()
    .filter((lensId, index, self) => self.indexOf(lensId) === index)
    .filter((lensId) => !loadedLenses.some((lens) => lens.id == lensId));

  if (missingLensIds.length > 0) {
    void getLensesById(missingLensIds);
  }
};

export const getReportsWithUserNames = async (
  filters: Record<IReportGalleryFilters> & Readonly<IReportGalleryFilters>,
  pagination: Record<IReportGalleryPagination> & Readonly<IReportGalleryPagination>,
  entityId: number,
  nextGenGalleryEnabled: boolean,
  currentUsers: List<IRecordOf<IReportCreator>>,
  getReports: (url: string) => Promise<IPaginatedReportsResponse>,
  getUsers: (
    reports: IReportResponse[],
    getUserById: (id: string) => Promise<IReportCreator>
  ) => Promise<[IReportResponse[], IReportCreator[]]>,
  getUserById: (string) => Promise<IReportCreator>,
  loadMissingLenses: (reports: IReportResponse[], loadedLenses: List<IRecordOf<IReportDatasetLens>>) => Promise<void>,
  currentLenses: List<IRecordOf<IReportDatasetLens>>
): Promise<IReportResponse[]> => {
  const search = filters.search === null ? '' : filters.search;
  let query = `entityId=${entityId}&createdBy=${filters.createdBy}&visibility=${filters.visibility}&sortBy=${filters.sort}`;

  // Add search,offset and limit parameters for next gen reports query
  if (nextGenGalleryEnabled) {
    query = `entityId=${entityId}&search=${search}&createdBy=${filters.createdBy}&visibility=${filters.visibility}&sortBy=${filters.sort}&limit=${pagination.current.limit}&offset=${pagination.current.offset}`;
  }

  reportActionCreators.loadReports();

  let lensTask: Promise<void>;
  try {
    const paginatedReports = await getReports(`${reportsV3ControllerUrl}?${query}`);
    reportActionCreators.setPagination({
      meta: { ...paginatedReports.meta, total: paginatedReports.meta.total <= 0 ? 1 : paginatedReports.meta.total },
      current: { ...pagination.current }
    });
    const reports = paginatedReports.reports;

    lensTask = loadMissingLenses(
      reports.filter((r) => !!r.lensId),
      currentLenses
    );

    if (nextGenGalleryEnabled) {
      const reportsWithCreatedUserName = reports.map((report) => ({
        ...report,
        createdByUser: currentUsers.find((user) => user.uuid === report.createdBy)
      }));

      const [reportsWithAllCreatedUserName, missingUserData] = await getUsers(reportsWithCreatedUserName, getUserById);
      reportActionCreators.loadReportCreatorsSuccess(missingUserData);

      reportActionCreators.loadReportsSuccess(reportsWithAllCreatedUserName);
      return reportsWithAllCreatedUserName;
    }

    reportActionCreators.loadReportsSuccess(reports);
    return reports;
  } catch (err) {
    reportActionCreators.loadReportsError({ message: err.message });
    throw err;
  } finally {
    await lensTask;
  }
};

const postReportExport = async (
  { locale, reportConfiguration, savedView, report, reportingUrl, savedViewUrl, friendlyColumnNames, sourceColumns },
  filtered: boolean
) =>
  getOgqlQueries({
    reportConfiguration: createReportConfigurationFactory(
      reportConfigForExport(reportConfiguration, sourceColumns, filtered)
    ),
    isExporting: true
  }).then(async (queries) =>
    platform
      .url(`/api/ogql/v0/exports?view_id=${report.viewId}`)
      .post({
        file_name: `${report.title}-${savedView ? savedView.title : ''}.csv`,
        query: queries.detailTableQuery,
        reporting_url: reportingUrl,
        saved_view_name: savedView ? savedView.title : '',
        saved_view_url: savedViewUrl,
        view_id: report.viewId,
        friendly_column_names: friendlyColumnNames
      })
      .json()
      .then(modalActionCreators.resetModalState)
      .then((_ignore) => {
        reportActionCreators.toggleMuiToast({
          type: MuiToastTypes.SUCCESS,
          headerText: locale.exportModal.toasts.titleSuccess,
          bodyText: locale.exportModal.toasts.bodySuccess
        });
      })
      .catch((_ignore) => {
        reportActionCreators.toggleMuiToast({
          type: MuiToastTypes.FAILURE,
          headerText: locale.exportModal.toasts.titleFailure,
          bodyText: locale.exportModal.toasts.bodyFailure
        });
      })
  );

// we have multiple export effects to be triggered from different places
// however, they all share some data in common
export const selectExportBasics = createSelector(
  [
    reportLocaleText,
    selectCurrentEntity,
    selectCurrentReport,
    selectReportConfigWithMetadata,
    selectDetailTableColumnNamesMap,
    selectReportExplorerPath
  ],
  (locale, currentEntity, report, reportConfiguration, friendlyColumnNames, reportExplorerPath) => ({
    friendlyColumnNames,
    locale,
    entitySubdomain: currentEntity.subdomain,
    report,
    reportConfiguration: produce(reportConfiguration, (draft) => {
      // Delphius only gets detail table exports .. no group by.
      draft.detailTableQuery.groupByColumns = [];
    }),
    reportingUrl: addOrigin(reportExplorerPath(currentEntity.subdomain, report.id))
  })
);

/**
 * A swap carrying a mapping goes through `change-dataset`, which rewrites every configuration and
 * repoints the report in one transaction. A bare dataset id stays on the plain attribute update,
 * which is all a fully compatible swap — or an Account Summary report — needs.
 */
export type UpdateReportDatasetPayload = string | { datasetId: string; mapping: IDatasetChangeMapping };

interface AppliedDatasetSelection {
  report: IReportResponse;
  /** Only the `change-dataset` path rewrites configurations; a plain pointer update keeps the current one. */
  newDefaultReportConfigurationId?: string;
}

const applyDatasetSelection = async (
  report: IRecordOf<IReportResponse>,
  payload: UpdateReportDatasetPayload,
  reportConfigurationId?: string
): Promise<AppliedDatasetSelection> => {
  const lensOrViewAttr = report.lensId ? 'lensId' : 'viewId';

  if (typeof payload === 'string') {
    return { report: await reportEffects.updateReport({ ...report.toJS(), [lensOrViewAttr]: payload }) };
  }

  const { rewritten } = await changeReportDataset(report.id, payload.datasetId, payload.mapping, reportConfigurationId);

  // The configuration table is insert-only, so the endpoint rewrites every configuration onto a *new*
  // row and repoints the report at the new default. The entry without a `savedViewId` is that default.
  const newDefaultReportConfigurationId = rewritten.find((rewrite) => !rewrite.savedViewId)?.newReportConfigurationId;

  // The endpoint returns the change records rather than the report, so the saved shape is the current
  // report with the two fields it moved. Carrying the new configuration id is what makes "Return to
  // Report Details" work: `reportExplorerRoute` navigates to `report.defaultReportConfiguration`, so
  // leaving the pre-swap id here sent the user to the un-rewritten configuration and the report
  // failed to load on its old column references — until a full reload re-read the report row.
  return {
    report: {
      ...report.toJS(),
      [lensOrViewAttr]: payload.datasetId,
      ...(newDefaultReportConfigurationId && { defaultReportConfiguration: newDefaultReportConfigurationId })
    } as IReportResponse,
    newDefaultReportConfigurationId
  };
};

export const reportEffects = composeEffects('report', (compose) => ({
  getReport: compose.statefulEffect(selectReports, async (reports, e: { reportId: string }): Promise<IReport> => {
    const existingReport = reports.find((r) => r.allowedActions && r.id === e.reportId);
    if (existingReport) {
      return existingReport;
    }

    return transport
      .url(`${reportsControllerUrl}/${e.reportId}`)
      .get()
      .json<IReport>()
      .then((report) => {
        reportActionCreators.loadReportSuccess(report);
        return report;
      })
      .catch((err) => {
        reportActionCreators.loadReportError({ status: err.status, message: err.message });
        throw err;
      });
  }),

  getReports: compose.statefulEffect(
    createSelector(
      [
        selectFilters,
        selectPagination,
        getNumericalEntityId,
        selectNextGenGalleryPhase1Enabled,
        selectReportCreators,
        selectDatasetLensesForReports
      ],
      (filters, pagination, entityId, selectNextGenGalleryPhase1Enabled, currentReportCreators, currentLenses) => ({
        filters,
        pagination,
        entityId,
        selectNextGenGalleryPhase1Enabled,
        currentReportCreators,
        currentLenses
      })
    ),
    ({ filters, pagination, entityId, selectNextGenGalleryPhase1Enabled, currentReportCreators, currentLenses }) =>
      getReportsWithUserNames(
        filters,
        pagination,
        entityId,
        selectNextGenGalleryPhase1Enabled,
        currentReportCreators,
        (url) => transport.url(url).get().json<IPaginatedReportsResponse>(),
        getReportUsers,
        getFullNameByUuid,
        loadMissingReportLenses,
        currentLenses
      )
  ),

  createReport: compose.effect(postReport),

  updateReport: compose.effect(async (e: IReport) => {
    return transport
      .url(`${reportsControllerUrl}/${e.id}`)
      .body(JSON.stringify(e))
      .put()
      .json<IReport>()
      .then((report) => {
        void SegmentEffects.track({ eventName: 'Report: Report: UpdateReport', props: report }).catch(
          (_ignore) => null
        );
        return report;
      })
      .catch(showPermissionErrorToast);
  }),

  updateReportTitleAndDescription: compose.statefulPayloadlessEffect(
    createSelector([selectCurrentReport, selectModifiedReport, reportLocaleText], (report, unsavedReport, locale) => ({
      report,
      unsavedReport,
      locale
    })),
    ({ report, unsavedReport, locale }) =>
      reportEffects
        .updateReport({
          ...report.toJS(),
          title: unsavedReport.title,
          description: unsavedReport.description
        })
        .then((savedReport) => reportActionCreators.loadReportsSuccess([savedReport]))
        .catch((_err) => {
          reportActionCreators.toggleMuiToast({
            type: MuiToastTypes.FAILURE,
            headerText: locale.report.settings.toastFailHeader,
            bodyText: locale.report.settings.toastFailBody
          });
        })
        .finally(() => reportExplorerActionCreators.onEditCardModeChange(SettingsCardMode.DefaultRead))
  ),

  updateReportVisibility: compose.statefulPayloadlessEffect(
    createSelector([selectCurrentReport, selectModifiedReport, reportLocaleText], (report, unsavedReport, locale) => ({
      report,
      unsavedReport,
      locale
    })),

    ({ report, unsavedReport, locale }) =>
      reportEffects
        .updateReport({
          ...report.toJS(),
          visibility: unsavedReport.visibility
        })
        .then((savedReport) => reportActionCreators.loadReportsSuccess([savedReport]))
        .catch((_err) => {
          reportActionCreators.toggleMuiToast({
            type: MuiToastTypes.FAILURE,
            headerText: locale.report.settings.toastFailHeader,
            bodyText: locale.report.settings.toastFailBody
          });
        })
        .finally(() => reportExplorerActionCreators.onEditCardModeChange(SettingsCardMode.DefaultRead))
  ),

  updateReportDataset: compose.statefulEffect(
    createSelector(
      [selectCurrentReport, selectCurrentReportConfiguration, reportLocaleText],
      (report, reportConfiguration, locale) => ({
        report,
        reportConfiguration,
        locale
      })
    ),
    async ({ report, reportConfiguration, locale }, payload: UpdateReportDatasetPayload) => {
      return applyDatasetSelection(report, payload, reportConfiguration?.id)
        .then(async ({ report: savedReport, newDefaultReportConfigurationId }) => {
          // The change-dataset endpoint syncs embeds itself, so only the plain update needs this.
          if (typeof payload === 'string') {
            void syncEmbedByReportIds({ ids: [savedReport.id], messageId: 'DATASET_CHANGE' });
          }

          if (report.lensId) {
            await reportDimensionalEffects.getReportDatasetLenses({ reportId: savedReport.id });
          } else if (report.viewId) {
            reportExplorerActionCreators.receivedReportView(await reportOgqlView({ reportId: savedReport.id }));
          }

          // Update the report in state
          reportActionCreators.loadReportSuccess(savedReport);

          // `getReportConfiguration` serves report history before the network, so any cached copy
          // still under the pre-swap id would be handed back in place of the rewritten one.
          reportHistoryActionCreators.clearReportHistory();

          // A dataset swap can leave stale storage-column references (organizedBy, showColumn,
          // detailTableQuery.columns) that only the server-side cleanup on GET /reportConfigurations
          // resolves — and that cleanup never persists to the DB, so it has to run on every fetch.
          // `refreshReportConfiguration` alone re-runs queries against whatever configuration is
          // already in the store, which at this point is still the pre-swap copy: charts and totals
          // kept failing on stale columns until the user did a full page reload. Re-fetching first is
          // what actually clears them.
          //
          // Read by the rewritten id, never the one in the store: the swap wrote the mapped
          // configuration to a new row, so the id held here still resolves to the un-rewritten copy.
          // Declared non-nullable but null at runtime on the boolean path, the same contract the
          // request path acknowledges by reading `reportConfiguration?.id`. Dereferencing it threw
          // *after* the dataset pointer had been saved, so a swap that worked was reported as failed.
          // Widened rather than optional-chained on the declared type, which the linter reads as an
          // unnecessary guard precisely because the type is the thing that is wrong here.
          const currentConfiguration = reportConfiguration as IReportConfiguration | null;
          const configurationIdToRefresh = newDefaultReportConfigurationId ?? currentConfiguration?.id;
          if (configurationIdToRefresh) {
            const freshReportConfiguration = await getReportConfigurationById(configurationIdToRefresh).then(
              createReportConfigurationFactory
            );
            reportConfigurationActionCreators.setCurrentReportConfigurationAndForceRefresh(freshReportConfiguration);
          }

          reportActionCreators.toggleMuiToast({
            type: MuiToastTypes.SUCCESS,
            headerText: locale.report.settings.toastDatasetUpdatedSuccessHeader,
            bodyText: locale.report.settings.toastDatasetUpdatedSuccessBody
          });
        })
        .catch((_err) => {
          reportActionCreators.toggleMuiToast({
            type: MuiToastTypes.FAILURE,
            headerText: locale.report.settings.toastDatasetUpdatedFailHeader,
            bodyText: locale.report.settings.toastDatasetUpdatedFailBody
          });
        })
        .finally(() => reportExplorerActionCreators.onEditCardModeChange(SettingsCardMode.DefaultRead));
    }
  ),
  deleteReport: compose.effect(async (e: { report: IReportResponse }) =>
    deleteReportById({ reportId: e.report.id })
      .then(() => {
        reportActionCreators.deleteReportSuccess(e.report);
      })
      .catch((err) => {
        reportActionCreators.deleteReportError(err);
        return Promise.reject(err instanceof Error ? err : new Error(String(err)));
      })
  ),

  createReportAndRedirect: compose.statefulEffect(
    createSelector(
      [selectNewReport, selectCurrentEntity, reportLocaleText],
      (newReport, currentEntity, localeText) => ({
        newReport,
        currentEntity,
        localeText
      })
    ),
    async (
      { newReport, currentEntity, localeText },
      e: { isDimensional: boolean; isMultiDataset: boolean; navigate: NavigateFunction }
    ) => {
      const startTime = moment();
      if (e.isMultiDataset) {
        modalActionCreators.setModalComponentType(ReportModalTypes.REPORT_CREATING_MDS);
      } else {
        modalActionCreators.setModalComponentType(ReportModalTypes.REPORT_CREATING);
      }
      const reportCreatePayload = ReportCreatePayload({
        title: newReport.title,
        description: newReport.description,
        viewId: newReport.viewId,
        lensId: newReport.lensId,
        entityId: newReport.entityId,
        parentFolderId: newReport.parentFolderId,
        isDimensional: e.isDimensional,
        isMultiDataset: e.isMultiDataset
      });

      return postReport(reportCreatePayload)
        .then(({ id }: IReport) => {
          const { subdomain } = currentEntity;
          const timerId = setInterval(() => {
            if (moment().diff(startTime) > 1000) {
              clearInterval(timerId);
              redirectToCreatedReport(id, subdomain, e.navigate);
            }
          }, 500);
        })
        .catch((err) => {
          modalActionCreators.resetModalState();
          switch (err.status) {
            case 404:
            case 408: {
              reportActionCreators.toggleMuiToast({
                type: MuiToastTypes.FAILURE,
                headerText: localeText.create.datasetUnavailable,
                bodyText: localeText.create.selectAnotherDataset
              });
              break;
            }
            case 401:
            case 403:
            case 400:
            default: {
              e.navigate(reportGalleryPathReactRouterVersion(currentEntity.id), {
                state: {
                  toastError: {
                    headerText: localeText.create.reportCreationFailure,
                    bodyText: localeText.create.somethingWentWrong
                  } satisfies ToastPayload
                }
              });
              reportCreateActionCreators.resetReportCreateState();
              break;
            }
          }
        })
        .then(reportCreateActionCreators.resetReportCreateState);
    }
  ),

  exportReportFromReportExplorer: compose.statefulEffect(
    createSelector(
      [selectExportBasics, selectCurrentSavedView, selectSavedViewPath, selectCurrentSourceColumns],
      (basics, currentSavedView, savedViewPath, sourceColumns) => ({
        ...basics,
        savedView: currentSavedView,
        savedViewUrl: currentSavedView
          ? addOrigin(savedViewPath(basics.entitySubdomain, basics.reportConfiguration.id, currentSavedView))
          : '',
        sourceColumns
      })
    ),
    async (selectors, filtered: boolean) => postReportExport(selectors, filtered)
  ),

  exportReportFromSavedViewEdit: compose.statefulEffect(
    createSelector(
      [selectExportBasics, selectCurrentSavedViewEditSavedView, selectCurrentSourceColumns],
      (basics, currentSavedViewEditSavedView, sourceColumns) => ({
        ...basics,
        savedView: currentSavedViewEditSavedView,
        savedViewUrl: addOrigin(
          reportSavedViewEditPath(basics.entitySubdomain, basics.report.id, currentSavedViewEditSavedView.id)
        ),
        sourceColumns
      })
    ),
    async (selectors, filtered: boolean) => postReportExport(selectors, filtered)
  ),
  performColumnSearchQuery: compose.statefulEffect(
    createSelector(
      [
        selectCurrentReportConfiguration,
        selectUseLargeScaleQueryLimit,
        reportLocaleText,
        selectUseQueryService,
        selectCurrentTemplateId,
        selectCurrentLens,
        selectIsInStandardReport
      ],
      (
        reportConfiguration,
        useLargeScaleQueryLimit,
        reportMsgs,
        useQueryService,
        templateId,
        lens,
        isStandardTemplate
      ) => ({
        reportConfiguration,
        useLargeScaleQueryLimit,
        reportMsgs,
        useQueryService,
        templateId,
        lens,
        isStandardTemplate
      })
    ),
    async (
      {
        reportConfiguration,
        useLargeScaleQueryLimit,
        reportMsgs,
        useQueryService,
        templateId,
        lens,
        isStandardTemplate
      },
      e: { stringToSearchFor: string; column: any }
    ) => {
      const { column, stringToSearchFor } = e;
      return columnSearchQuery({
        column,
        stringToSearchFor,
        reportConfiguration,
        useLargeScaleQueryLimit,
        reportMsgs,
        useQueryService,
        templateId,
        lensId: lens && lens.id,
        isStandardTemplate
      });
    }
  ),

  createReportPermalink: compose.statefulEffect(
    createSelector(
      [selectCurrentReportConfiguration, reportLocaleText, selectCurrentSavedView],
      (currentReportConfiguration, localeText, savedView) => ({
        currentReportConfiguration,
        localeText,
        savedView
      })
    ),
    async ({ currentReportConfiguration, localeText, savedView }, _e: { isDimensional: boolean }) => {
      const payload =
        savedView && savedView.id
          ? {
              reportConfiguration: currentReportConfiguration,
              savedViewId: savedView.id
            }
          : {
              reportConfiguration: currentReportConfiguration
            };
      return platform
        .url(reportPermalinkUrl)
        .post(payload)
        .json()
        .then((response) => {
          const typedResponse = response as { urls: { public: string; publicPermalink?: string } };
          const { public: snapshotLink, publicPermalink } = typedResponse.urls;
          shareLinkActionCreators.setShareLinks({
            snapshotLink,
            permalinkLink: publicPermalink ?? null
          });
          modalActionCreators.setModalComponentType(ReportModalTypes.REPORT_SHARE);
        })
        .catch((_err) => {
          reportActionCreators.toggleMuiToast({
            type: MuiToastTypes.FAILURE,
            headerText: localeText.shareLink.shareLinkFailureToastTitle,
            bodyText: localeText.shareLink.shareLinkFailureToastMessage,
            dataTest: `toast-transparency-permalink-failure`
          });
        });
    }
  ),

  createCustomReportAndRedirect: compose.statefulEffect(
    createSelector(
      [selectNewReport, selectCurrentReport, selectCurrentEntity, reportLocaleText],
      (newReport, sourceReport, currentEntity, localeText) => ({
        newReport,
        sourceReport,
        currentEntity,
        localeText
      })
    ),
    async (
      { newReport, sourceReport, currentEntity, localeText },
      e: { copySavedViews: boolean; navigate: NavigateFunction; parentFolderId?: string | null }
    ) => {
      const { copySavedViews, navigate, parentFolderId } = e;
      if (sourceReport && sourceReport.isMultiDataset) {
        modalActionCreators.setModalComponentType(ReportModalTypes.REPORT_CREATING_MDS);
      } else {
        modalActionCreators.setModalComponentType(ReportModalTypes.REPORT_CREATING);
      }

      return createReportCopy(sourceReport.id, newReport.title, newReport.description, copySavedViews, parentFolderId)
        .then((newReportResponse: IReport) => {
          navigate(makeReportExplorerPathReactRouterVersion(currentEntity.subdomain, newReportResponse.id), {
            replace: true
          });
          modalActionCreators.resetModalState();
          reportHistoryActionCreators.clearReportHistory();
        })
        .catch((err) => {
          modalActionCreators.resetModalState();
          switch (err.status) {
            case 404:
            case 408: {
              reportActionCreators.toggleMuiToast({
                type: MuiToastTypes.FAILURE,
                headerText: localeText.create.datasetUnavailable,
                bodyText: localeText.create.selectAnotherDataset
              });
              break;
            }
            case 401:
            case 403:
            case 400:
            default: {
              navigate(reportGalleryPathReactRouterVersion(currentEntity.id), {
                state: {
                  toastError: {
                    headerText: localeText.create.reportCreationFailure,
                    bodyText: localeText.create.somethingWentWrong
                  } satisfies ToastPayload
                }
              });
              break;
            }
          }
        })
        .finally(() => {
          modalActionCreators.resetModalState();
          reportCreateActionCreators.resetReportCreateState();
        });
    }
  ),

  createCustomReportFromStandardAndRedirect: compose.statefulEffect(
    createSelector(
      [
        selectNewReport,
        selectCurrentLens,
        selectCurrentReport,
        selectCurrentEntity,
        reportLocaleText,
        selectReportExplorerPath,
        selectCurrentReportConfiguration,
        selectSavedViews
      ],
      (newReport, lens, sourceReport, currentEntity, localeText, reportExplorerPath, reportConfig, savedViews) => ({
        newReport,
        lens,
        sourceReport,
        currentEntity,
        localeText,
        reportExplorerPath,
        reportConfig,
        savedViews
      })
    ),
    async (
      {
        newReport,
        lens,
        sourceReport,
        currentEntity,
        localeText,
        reportExplorerPath,
        reportConfig,
        savedViews: _savedViews
      },
      e: {
        isDimensional: boolean;
        copySavedViews: boolean;
        copyAccess: boolean;
        templateSavedViews?: ITemplatedSavedView[];
        parentFolderId?: string | null;
      }
    ) => {
      if (sourceReport && sourceReport.isMultiDataset) {
        modalActionCreators.setModalComponentType(ReportModalTypes.REPORT_CREATING_MDS);
      } else {
        modalActionCreators.setModalComponentType(ReportModalTypes.REPORT_CREATING);
      }

      const parentFolderIdNumber = e.parentFolderId && e.parentFolderId !== 'none' ? Number(e.parentFolderId) : null;

      return postReport(
        ReportCreatePayload({
          title: newReport.title,
          description: newReport.description,
          lensId: lens ? lens.id : sourceReport.lensId,
          viewId: sourceReport ? sourceReport.viewId : null,
          entityId: currentEntity.entity_id,
          isDimensional: e.isDimensional || false,
          defaultReportConfiguration: reportConfig ? createReportConfigurationFactory(reportConfig) : null,
          parentFolderId: parentFolderIdNumber
        })
      )
        .then(async (newReportResponse: IReport) => {
          if (e.copySavedViews && e.templateSavedViews) {
            await Promise.all(
              e.templateSavedViews.map((view) => {
                return copySavedViewForStandardReport(view, newReportResponse.id);
              })
            ).catch((error) => {
              throw error;
            });
          }
          return Promise.resolve(newReportResponse);
        })
        .then((newReportResponse: IReport) => {
          hardNavigate(reportExplorerPath(currentEntity.subdomain, newReportResponse.id));
          modalActionCreators.resetModalState();
          reportHistoryActionCreators.clearReportHistory();
        })
        .catch((err) => {
          modalActionCreators.resetModalState();
          switch (err.status) {
            case 404:
            case 408: {
              reportActionCreators.toggleMuiToast({
                type: MuiToastTypes.FAILURE,
                headerText: localeText.create.datasetUnavailable,
                bodyText: localeText.create.selectAnotherDataset
              });
              break;
            }
            case 401:
            case 403:
            case 400:
            default: {
              console.error(err);
              hardNavigate(reportGalleryPath(currentEntity.id));
              reportActionCreators.toggleMuiToast({
                type: MuiToastTypes.FAILURE,
                headerText: localeText.create.reportCreationFailure,
                bodyText: localeText.create.somethingWentWrong
              });
              reportCreateActionCreators.resetReportCreateState();
              break;
            }
          }
        })
        .then(reportCreateActionCreators.resetReportCreateState);
    }
  )
}));

export function redirectToCreatedReport(
  reportId: IReport['id'],
  subdomain: IEntity['subdomain'],
  navigate: NavigateFunction
) {
  if (reportId) {
    reportActionCreators.toggleMuiToast(null);
    navigate(makeReportExplorerPathReactRouterVersion(subdomain, reportId), { replace: true });
    modalActionCreators.resetModalState();
  }
}
